import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Globe, Package, QrCode, Receipt, Tv, User, type LucideIcon } from "lucide-react";

const LEFT_SURFACES = ["pos", "storefront", "qr"] as const;
const RIGHT_SURFACES = ["waiter", "screens", "warehouse"] as const;

const SURFACE_ICONS: Record<string, LucideIcon> = {
  pos: Receipt,
  storefront: Globe,
  qr: QrCode,
  waiter: User,
  screens: Tv,
  warehouse: Package,
};

function Surface({ surfaceKey, side, delay }: { surfaceKey: string; side: "left" | "right"; delay: number }) {
  const { t } = useTranslation();
  const Icon = SURFACE_ICONS[surfaceKey];

  const connector = (
    <span
      aria-hidden="true"
      className="hidden lg:flex flex-1 items-center gap-1.5 min-w-[24px]"
      style={{ flexDirection: side === "left" ? "row" : "row-reverse" }}
    >
      <span className="h-px flex-1 bg-gradient-to-r from-primary/10 to-primary/45" />
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
    </span>
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: side === "left" ? -20 : 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay }}
      className="flex items-center gap-2"
      style={{ flexDirection: side === "left" ? "row" : "row-reverse" }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-dark-border bg-dark-card px-4 py-3.5 transition-colors hover:border-primary/40">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" strokeWidth={1.9} />
        </span>
        <span className="truncate text-[14.5px] font-semibold text-white">
          {t(`onemenu.surfaces.${surfaceKey}`)}
        </span>
      </div>
      {connector}
    </motion.div>
  );
}

export function OneMenu() {
  const { t } = useTranslation();
  const moments = t("onemenu.moments", { returnObjects: true }) as { title: string; desc: string }[];

  return (
    <section id="how" className="relative bg-dark-bg py-[96px] overflow-hidden">
      {/* Background grid, matching the hero */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,173,181,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,173,181,1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[420px] w-[680px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/15 blur-[130px] pointer-events-none"
      />

      <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">
            {t("onemenu.tag")}
          </p>
          <h2 className="text-[32px] font-bold leading-[1.15] tracking-[-0.02em] text-white sm:text-[44px]">
            {t("onemenu.title")}
          </h2>
          <p className="mx-auto mt-4 max-w-[600px] text-[16px] leading-relaxed text-white/55 sm:text-[18px]">
            {t("onemenu.subtitle")}
          </p>
        </motion.div>

        {/* ── Hub and spokes ── */}
        <div className="mt-14 grid items-center gap-6 lg:grid-cols-[1fr_268px_1fr] lg:gap-8">
          <div className="order-2 grid gap-3.5 sm:grid-cols-2 lg:order-1 lg:grid-cols-1">
            {LEFT_SURFACES.map((key, i) => (
              <Surface key={key} surfaceKey={key} side="left" delay={0.1 + i * 0.08} />
            ))}
          </div>

          {/* Hub */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="order-1 lg:order-2"
          >
            <div className="relative mx-auto max-w-[280px] rounded-2xl border border-primary/40 bg-dark-card p-6 text-center shadow-2xl shadow-primary/10">
              <motion.span
                aria-hidden="true"
                className="absolute inset-0 rounded-2xl border border-primary/30"
                animate={{ opacity: [0.6, 0.15, 0.6], scale: [1, 1.035, 1] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
              />
              <p className="font-raleway text-[26px] font-bold leading-tight text-white">
                {t("onemenu.hub_title")}
              </p>
              <p className="mt-1 font-raleway text-[26px] font-bold leading-tight text-gradient">
                {t("onemenu.hub_sub")}
              </p>
            </div>
          </motion.div>

          <div className="order-3 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-1">
            {RIGHT_SURFACES.map((key, i) => (
              <Surface key={key} surfaceKey={key} side="right" delay={0.1 + i * 0.08} />
            ))}
          </div>
        </div>

        {/* ── The three moments that matter ── */}
        <div className="mt-16 grid gap-5 md:grid-cols-3">
          {moments.map((moment, i) => (
            <motion.div
              key={moment.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="rounded-2xl border border-dark-border bg-dark-card/60 p-6 transition-colors hover:border-primary/30"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 font-raleway text-[13px] font-bold text-primary">
                {i + 1}
              </span>
              <h3 className="mt-4 text-[18px] font-bold leading-snug text-white">{moment.title}</h3>
              <p className="mt-2.5 text-[14px] leading-[1.65] text-white/55">{moment.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
